// Start the product film muted when it first enters view. Player controls remain available.
customElements.whenDefined('video-media').then(() => {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  document.querySelectorAll('[data-pdp-muted-autoplay] video-media').forEach(media => {
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      observer.disconnect();
      const video = media.querySelector('video');
      if (video) video.muted = true;
      media.play();
    }, {threshold: 0.3});
    observer.observe(media);
  });
});

// Keep the merchandised option order (the section's "Option order" setting,
// comma separated labels) when Shopify replaces the product information.
const hero = document.querySelector('.shopify-section--pdp-v2 product-rerender[data-option-order]');
if (hero) {
  const order = hero.dataset.optionOrder.split(',').map(label => label.trim()).filter(Boolean);
  const observer = new MutationObserver(arrangeOptions);
  function arrangeOptions() {
    observer.disconnect();
    const cards = [...hero.querySelectorAll('a.variant-card-row')];
    const labels = cards.map(card => card.querySelector('.variant-card-row__title')?.textContent.trim());
    if (cards.length === order.length && order.every(label => labels.includes(label)) && cards.every(card => card.parentElement === cards[0].parentElement)) {
      const parent = cards[0].parentElement;
      order.forEach(label => parent.append(cards[labels.indexOf(label)]));
    }
    observer.observe(hero, {childList: true, subtree: true});
  }
  arrangeOptions();
}

// Okendo's stored question prompt is an admin instruction. Label the rider
// activity consistently with the approved design; leave the answers intact.
const reviews = document.querySelector('.pdp-v2-reviews__widget');
if (reviews) {
  const observer = new MutationObserver(labelActivity);
  function labelActivity() {
    observer.disconnect();
    reviews.querySelectorAll('.oke-w-selectAttr-item-title').forEach(label => {
      if (label.textContent.trim() === 'Choose options below') label.textContent = 'Used for: ';
    });
    observer.observe(reviews, {childList: true, subtree: true});
  }
  labelActivity();
}

// Enhance the real review list as soon as Okendo renders it, including initial load.
class PdpReviewCarousel extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.list = null;
    this.hovered = false;
    this.lastTime = 0;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.direction = 1;
    this.paused = false;
    this.visible = false;
    this.resumeAt = 0;
    const signal = this.abort.signal;
    const refresh = () => {
      const list = this.querySelector('.oke-w-reviews-list');
      if (!list || list === this.list) return;
      this.list = list;
      this.position = list.scrollLeft;
      list.tabIndex = 0;
      list.setAttribute('aria-label', 'Customer reviews. Use the arrow keys to browse.');
      this.querySelector('.pdp-v2-reviews__controls').hidden = false;
      list.addEventListener('scroll', () => {this.position = list.scrollLeft;}, {passive:true, signal});
      this.visibilityObserver.disconnect();
      this.visibilityObserver.observe(list);
    };
    this.visibilityObserver = new IntersectionObserver(entries => {this.visible = entries[0].isIntersecting;}, {threshold:0.1});
    this.observer = new MutationObserver(refresh);
    this.observer.observe(this, {childList:true, subtree:true});
    const pauseButton = this.querySelector('[data-review-pause]');
    const updateButton = () => {
      const stopped = this.paused || this.motion.matches;
      pauseButton.setAttribute('aria-pressed', String(stopped));
      pauseButton.setAttribute('aria-label', this.motion.matches ? 'Review autoplay disabled for reduced motion' : stopped ? 'Play reviews' : 'Pause reviews');
      pauseButton.disabled = this.motion.matches;
    };
    this.motion.addEventListener('change', updateButton, {signal});
    this.addEventListener('click', event => {
      const button = event.target.closest('button');
      if (!button || !this.list) return;
      if (button.hasAttribute('data-review-pause')) {this.paused = !this.paused; updateButton();}
      if (button.hasAttribute('data-review-prev')) this.step(-1);
      if (button.hasAttribute('data-review-next')) this.step(1);
    }, {signal});
    this.addEventListener('keydown', event => {
      if (event.target !== this.list || !['ArrowLeft','ArrowRight'].includes(event.key)) return;
      event.preventDefault(); this.step(event.key === 'ArrowRight' ? 1 : -1);
    }, {signal});
    this.addEventListener('pointerenter', () => {this.hovered = true;}, {signal});
    this.addEventListener('pointerleave', () => {this.hovered = false;}, {signal});
    this.addEventListener('pointerdown', () => {this.resumeAt = performance.now() + 8000;}, {passive:true, signal});
    this.addEventListener('wheel', () => {this.resumeAt = performance.now() + 8000;}, {passive:true, signal});
    const tick = time => {
      const elapsed = Math.min(time - (this.lastTime || time), 50);
      this.lastTime = time;
      const moving = this.list && this.visible && !this.paused && !this.motion.matches && !this.hovered && !this.contains(document.activeElement) && !document.hidden && time > this.resumeAt;
      if (this.list) this.list.dataset.autoplay = String(!!moving);
      if (moving) {
        const max = this.list.scrollWidth - this.list.clientWidth;
        if (max > 0) {
          this.position += this.direction * elapsed * 0.025;
          if (this.position >= max) {this.position = max; this.direction = -1;}
          if (this.position <= 0) {this.position = 0; this.direction = 1;}
          this.list.scrollLeft = this.position;
        }
      }
      this.frame = requestAnimationFrame(tick);
    };
    refresh(); updateButton(); this.frame = requestAnimationFrame(tick);
  }
  step(direction) {
    this.resumeAt = performance.now() + 8000;
    this.list.scrollBy({left:direction * ((this.list.firstElementChild?.getBoundingClientRect().width || 300) + 20), behavior:this.motion.matches ? 'auto' : 'smooth'});
  }
  disconnectedCallback() {
    this.abort?.abort(); this.observer?.disconnect(); this.visibilityObserver?.disconnect(); cancelAnimationFrame(this.frame);
  }
}
if (!customElements.get('pdp-review-carousel')) customElements.define('pdp-review-carousel', PdpReviewCarousel);
